import { useState } from "react";
import type { LayoutDoc } from "../../../shared/layout";
import type { BedtimeSettings, ScreenSchedule } from "../../../shared/models";
import { OWN_SCREENS } from "../../../shared/screens";
import { setDevicePref, useDevicePrefs } from "../../app/devicePrefs";
import { useHousehold } from "../../app/useHousehold";
import { useScreens } from "../../app/useScreens";
import { useUpdateAtlasSettings } from "../../atlas/atlasQueries";
import { useLayouts, useUpdateSettings } from "../../data/queries";
import { Icon, type IconName } from "../../ui/Icon";

const SLOTS: { key: keyof ScreenSchedule; label: string; help: string }[] = [
  { key: "morning", label: "Morning", help: "Starts the day. A pinned screen is released at this time." },
  { key: "day", label: "Day", help: "Most of the day." },
  { key: "night", label: "Night", help: "Evening and overnight; the wall also dims." },
];

/** Which screen shows when, how long until an untouched wall returns, and per-screen behaviour. */
export function ScreensSection() {
  const { settings, isAdmin } = useHousehold();
  const allLayouts = useLayouts().data ?? [];
  const screens = useScreens();
  const layouts = allLayouts.filter((l) => screens.isOn(l.id));
  const update = useUpdateSettings();
  const device = useDevicePrefs();
  const [schedule, setSchedule] = useState<ScreenSchedule>(settings.schedule);
  const [msg, setMsg] = useState<string | null>(null);
  const [bedtime, setBedtime] = useState<BedtimeSettings>(settings.bedtime);
  const [bedMsg, setBedMsg] = useState<string | null>(null);
  const saveBedtime = () =>
    update.mutate({ bedtime }, { onSuccess: () => setBedMsg("Bedtime saved"), onError: (e) => setBedMsg((e as Error).message) });

  const set = (slot: keyof ScreenSchedule, patch: Partial<ScreenSchedule["morning"]>) => setSchedule((s) => ({ ...s, [slot]: { ...s[slot], ...patch } }));
  const save = () =>
    update.mutate({ schedule }, { onSuccess: () => setMsg("Schedule saved"), onError: (e) => setMsg((e as Error).message) });

  return (
    <div className="form">
      <h3>Screens & schedule</h3>
      <ScreenSwitches layouts={allLayouts} />
      <hr />
      <h4>Schedule</h4>
      <p className="muted">
        The wall switches screens through the day and comes back to the scheduled screen when nobody has touched it for a while. Tap <strong>Pin screen</strong> on the wall to keep any screen up until the next morning.
      </p>
      <table className="table schedule-table">
        <thead>
          <tr>
            <th>Part of day</th>
            <th>Starts at</th>
            <th>Screen</th>
          </tr>
        </thead>
        <tbody>
          {SLOTS.map((s) => (
            <tr key={s.key}>
              <td>
                <strong>{s.label}</strong>
                <div className="muted small">{s.help}</div>
              </td>
              <td>
                <input className="input" type="time" value={schedule[s.key].start} disabled={!isAdmin} onChange={(e) => set(s.key, { start: e.target.value })} />
              </td>
              <td>
                <select className="input" value={schedule[s.key].layoutKey} disabled={!isAdmin} onChange={(e) => set(s.key, { layoutKey: e.target.value })}>
                  {layouts.map((l) => (
                    <option key={l.id} value={l.presetKey ?? l.id}>
                      {l.name}
                    </option>
                  ))}
                </select>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {isAdmin && (
        <div className="row">
          <button className="btn btn-primary" onClick={save} disabled={update.isPending}>
            Save schedule
          </button>
          {msg && <span className="muted">{msg}</span>}
        </div>
      )}
      <div className="field">
        <span>Bedtime</span>
        <div className="row">
          <input className="input" type="time" aria-label="Bedtime starts" value={bedtime.start} disabled={!isAdmin} onChange={(e) => setBedtime((b) => ({ ...b, start: e.target.value }))} />
          <span className="muted">to</span>
          <input className="input" type="time" aria-label="Bedtime ends" value={bedtime.end} disabled={!isAdmin} onChange={(e) => setBedtime((b) => ({ ...b, end: e.target.value }))} />
          {isAdmin && (
            <button className="btn" onClick={saveBedtime} disabled={update.isPending || (bedtime.start === settings.bedtime.start && bedtime.end === settings.bedtime.end)}>
              Save bedtime
            </button>
          )}
          {bedMsg && <span className="muted">{bedMsg}</span>}
        </div>
        <p className="muted small">While everyone’s asleep, a wall display left alone for a few minutes goes dark but for a dim clock, or turns its screen off. Each screen chooses which in Settings → Display.</p>
      </div>
      <div className="field">
        <span>Return to the scheduled screen after no touches for</span>
        <div className="chip-row wrap">
          {[0, 1, 2, 5, 10, 15, 30].map((m) => (
            <button key={m} className={`chip ${settings.idleReturnMinutes === m ? "on" : ""}`} disabled={!isAdmin} onClick={() => update.mutate({ idleReturnMinutes: m })}>
              {m === 0 ? "Never" : `${m} min`}
            </button>
          ))}
        </div>
      </div>
      <label className="field field-toggle">
        <span>
          Switch to the Alerts screen for dangerous weather
          <small className="muted">
            Tornado, severe thunderstorm and flash flood warnings, red flag and fire warnings, blizzards and other life-threatening alerts take over wall displays until they end or someone taps <strong>Got it</strong>.
          </small>
        </span>
        <input type="checkbox" className="switch" checked={settings.alertTakeover} disabled={!isAdmin} onChange={(e) => update.mutate({ alertTakeover: e.target.checked })} />
      </label>
      <hr />
      <h4>This screen</h4>
      <label className="field field-toggle">
        <span>
          Wall display mode
          <small className="muted">Follow the schedule, return when idle, and dim at night. Leave off on phones and laptops.</small>
        </span>
        <input type="checkbox" className="switch" checked={device.autoScreens} onChange={(e) => setDevicePref("autoScreens", e.target.checked)} />
      </label>
      <label className="field field-toggle">
        <span>
          Reduce visual effects
          <small className="muted">No background blur or animated weather. Try this on a Raspberry Pi if anything feels slow.</small>
        </span>
        <input type="checkbox" className="switch" checked={device.reduceEffects} onChange={(e) => setDevicePref("reduceEffects", e.target.checked)} />
      </label>
      <p className="muted small">To rearrange a screen’s widgets, open it on any large display and tap <strong>Edit layout</strong>.</p>
    </div>
  );
}

const LAYOUT_ICONS: Record<string, IconName> = {
  morning: "sun",
  "family-home": "home",
  "full-calendar": "calendar",
  "weather-focus": "cloud",
  chores: "star",
  kitchen: "utensils",
  photos: "image",
  night: "moon",
  alerts: "alert",
  outdoors: "mountain",
  news: "news",
};

/**
 * Each screen on or off for every display. Off takes it out of the
 * navigation and the schedule and stops everything it does in the background;
 * nothing is deleted, so turning it back on brings it back as it was.
 */
function ScreenSwitches({ layouts }: { layouts: LayoutDoc[] }) {
  const { settings, isAdmin } = useHousehold();
  const update = useUpdateSettings();
  const updateAtlas = useUpdateAtlasSettings();
  const { isOn } = useScreens();
  const [msg, setMsg] = useState<string | null>(null);
  const sorted = [...layouts].sort((a, b) => a.sortOrder - b.sortOrder);
  const layoutsOn = sorted.filter((l) => isOn(l.id)).length;
  const scheduled = new Set(Object.values(settings.schedule).map((s) => s.layoutKey));
  const busy = update.isPending || updateAtlas.isPending;

  const set = (id: string, on: boolean) => {
    setMsg(null);
    const fail = (e: unknown) => setMsg((e as Error).message);
    // The Living Atlas keeps its own master switch (also in Admin → Living Atlas).
    if (id === "atlas") return updateAtlas.mutate({ enabled: on }, { onError: fail });
    const off = settings.screens.off.filter((x) => x !== id);
    update.mutate({ screens: { off: on ? off : [...off, id] } }, { onError: fail });
  };

  const row = (id: string, name: string, icon: IconName, help: string, extra?: string, locked?: string) => {
    const on = isOn(id);
    return (
      <li key={id} className={`screen-switch ${on ? "" : "is-off"}`}>
        <label className="field field-toggle">
          <span>
            <span className="screen-switch-name">
              <Icon name={icon} size="1.15rem" /> {name}
            </span>
            <small className="muted">{help}</small>
            {extra && <small className="muted">{extra}</small>}
            {locked && <small className="muted">{locked}</small>}
          </span>
          <input type="checkbox" className="switch" checked={on} disabled={!isAdmin || busy || Boolean(locked)} onChange={(e) => set(id, e.target.checked)} aria-label={`Show ${name}`} />
        </label>
      </li>
    );
  };

  return (
    <div className="screen-switches">
      <p className="muted">
        Turn screens on or off for every display in the household. A screen that’s off leaves the navigation and the schedule, and everything it does in the background stops. Nothing is deleted: turn it back on and it’s just as you left it.
      </p>
      <h4>Screens</h4>
      <ul className="screen-switch-list">{OWN_SCREENS.map((s) => row(s.id, s.name, s.icon as IconName, s.blurb, isOn(s.id) ? s.stops.replace(/^Stops/, "Turning it off stops") : undefined))}</ul>
      <h4>Widget screens</h4>
      <ul className="screen-switch-list">
        {sorted.map((l) =>
          row(
            l.id,
            l.name,
            LAYOUT_ICONS[l.presetKey ?? ""] ?? "layout",
            l.presetKey === "alerts" ? "Weather alerts in full. Off also stops dangerous-weather alerts taking over the wall." : `${l.items.length} widget${l.items.length === 1 ? "" : "s"}`,
            isOn(l.id) && (scheduled.has(l.id) || (l.presetKey && scheduled.has(l.presetKey))) ? "On the schedule: while it’s off, the wall shows the default screen in its place." : undefined,
            isOn(l.id) && layoutsOn <= 1 ? "At least one widget screen stays on." : undefined,
          ),
        )}
      </ul>
      {msg && <p className="error-text">{msg}</p>}
    </div>
  );
}
